// Smooth scroll reveal system with fade-in and slide-up animations
import { useEffect, useState, useRef, useCallback } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  staggerDelay?: number;
  disabled?: boolean;
}

export const useScrollReveal = (options: UseScrollRevealOptions = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -100px 0px',
    staggerDelay = 100,
    disabled = false
  } = options;
  
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled || typeof window === 'undefined') {
      setIsVisible(true);
      return;
    }

    // Check for reduced motion preference and mobile
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth <= 768;
    
    if (prefersReducedMotion || isMobile) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [threshold, rootMargin, disabled]);

  const getRevealStyle = useCallback((index: number = 0) => {
    // Honor user's motion preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth <= 768;
    
    // Show content immediately on mobile and reduced motion
    if (prefersReducedMotion || isMobile) {
      return {
        opacity: 1,
        transition: 'opacity 0.2s ease-out',
      };
    }

    // Smooth fade-in and slide-up animation
    return {
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
      transitionDelay: `${index * staggerDelay}ms`,
      willChange: 'opacity, transform',
    };
  }, [isVisible, staggerDelay]);

  return { isVisible, elementRef, getRevealStyle };
};